import {TagSchemaItemType} from 'graphql-client/__generated/graphqlGlobalTypes';

import {BenchlingIconName} from './BenchlingIcon';

/**
 * Enum values are expected to be valid icon names acceptable by BenchlingIcon, or otherwise you may introduce
 * a mapping in IconAliases to map to a valid icon name.
 */
export enum FolderItemIcon {
  Antibody = 'antibody',
  ArrowZigZagDown = 'arrow-zig-zag-down',
  ArrowZigZagRight = 'arrow-zig-zag-right',
  Assembly = 'assembly',
  Bacteria = 'bacteria',
  BasicFolderItem = 'basic_folder_item',
  Beaker = 'beaker',
  BoxArrowRight = 'box-arrow-right',
  BrokenColumnsInBracket = 'broken-columns-in-bracket',
  BuildingPlus = 'building-plus',
  CellLine = 'cell-line',
  ChemicalCompound = 'chemical-compound',
  ChemicalConcatenation = 'chemical-concatenation',
  ChemicalHomology = 'chemical-homology',
  ChemicalLadder = 'chemical-ladder',
  Chromatography = 'chromatography',
  CircleWithThickSegment = 'circle-with-thick-segment',
  ClipboardInBrackets = 'clipboard-in-brackets',
  Compound = 'compound',
  DockBottom = 'dock-bottom',
  Entry = 'entry',
  Equipment = 'microscope',
  FileBarChart = 'file-bar-chart',
  FileInBrackets = 'file-in-brackets',
  FillGrid = 'fill-grid',
  Flask = 'flask',
  FlaskReceipt = 'flask-receipt',
  Flowchart = 'flowchart',
  GridDots = 'grid-dots',
  Human = 'human',
  Instrument = 'instrument',
  LightbulbInBrackets = 'lightbulb-in-brackets',
  Linear = 'linear',
  ListAsterisk = 'list-asterisk',
  ListCheckXmark = 'list-check-xmark',
  Material = 'material',
  Mixture = 'mixture',
  Molecule = 'molecule',
  MoleculeToBackbone = 'molecule-to-backbone',
  MoleculeToMolecule = 'molecule-to-molecule',
  MortarPestle = 'mortar-pestle',
  Mouse = 'mouse',
  NodesConverge = 'nodes-converge',
  OchemMolecule = 'ochem-molecule',
  OchemMoleculeBeaker = 'ochem-molecule-beaker',
  Oligo = 'oligo',
  OligoAlt = 'oligo-alt',
  OligoConjugate = 'network-wired',
  OligoV2 = 'oligo-v2',
  Part = 'part',
  Petri = 'petri',
  Plant = 'plant',
  Plasmid = 'plasmid',
  Powder = 'powder',
  PowderBeaker = 'powder-beaker',
  PrimerPair = 'primer_pair',
  Process = 'process',
  Protein = 'protein',
  ProteinAlt = 'protein-alt',
  ProteinMagnifyingGlass = 'protein-magnifying-glass',
  RNASequence = 'rna_sequence',
  Reaction = 'reaction-vessel',
  ReceiptFlask = 'receipt-flask',
  Recipe = 'recipe',
  RequestTemplate = 'request-template',
  Rna = 'rna',
  SampleSlide = 'sample-slide',
  Sequence = 'sequence',
  SmallMolecule = 'small-molecule',
  SmallMoleculeBeaker = 'small-molecule-beaker',
  Solution = 'solution',
  SquareBracketCheckEquals = 'square-bracket-check-equals',
  TableListAlt = 'table-list-alt',
  Template = 'template',
  TubeEmpty = 'tube-empty',
  Vial = 'vial',
  WellPlate = 'well-plate',
  Workflow = 'workflow',
  Worksheet = 'worksheet',
  Worm = 'worm',
}

/**
 * Keep in sync with the backend *SchemaIconId DGEnums
 * (defined in packages/benchling-domain-graph/src/benchling_domain_graph/sdl/schemas/ under *.graphql files)
 * and the _NON_ENTITY_ICON_NAMES dict in scripts/schemas/audit_icon_ids.py.
 */
export const FOLDER_ITEM_ICONS_BY_ITEM_TYPE: Record<TagSchemaItemType, Array<BenchlingIconName>> = {
  [TagSchemaItemType.AA_SEQUENCE]: [
    FolderItemIcon.Protein,
    FolderItemIcon.Linear,
    FolderItemIcon.BasicFolderItem,
  ],
  [TagSchemaItemType.ADC]: [FolderItemIcon.Antibody],
  [TagSchemaItemType.ANTIBODY]: [FolderItemIcon.Antibody],
  [TagSchemaItemType.BASIC_FOLDER_ITEM]: [
    FolderItemIcon.BasicFolderItem,
    FolderItemIcon.Sequence,
    FolderItemIcon.RNASequence,
    FolderItemIcon.OligoV2,
    FolderItemIcon.PrimerPair,
    FolderItemIcon.Protein,
    FolderItemIcon.Antibody,
    FolderItemIcon.Bacteria,
    FolderItemIcon.CellLine,
    FolderItemIcon.Chromatography,
    FolderItemIcon.Compound,
    FolderItemIcon.Human,
    FolderItemIcon.Linear,
    FolderItemIcon.Material,
    FolderItemIcon.Mouse,
    FolderItemIcon.Plant,
    FolderItemIcon.SampleSlide,
    FolderItemIcon.SmallMolecule,
    FolderItemIcon.SmallMoleculeBeaker,
    FolderItemIcon.Worm,
    FolderItemIcon.Equipment,
    FolderItemIcon.Flask,
    FolderItemIcon.MortarPestle,
    FolderItemIcon.Oligo,
    FolderItemIcon.Powder,
    FolderItemIcon.PowderBeaker,
    FolderItemIcon.Reaction,
    FolderItemIcon.Recipe,
    FolderItemIcon.Solution,
    FolderItemIcon.Template,
    FolderItemIcon.Worksheet,
    FolderItemIcon.ArrowZigZagDown,
    FolderItemIcon.ArrowZigZagRight,
    FolderItemIcon.Assembly,
    FolderItemIcon.Beaker,
    FolderItemIcon.BoxArrowRight,
    FolderItemIcon.BrokenColumnsInBracket,
    FolderItemIcon.BuildingPlus,
    FolderItemIcon.ChemicalCompound,
    FolderItemIcon.ChemicalConcatenation,
    FolderItemIcon.ChemicalHomology,
    FolderItemIcon.ChemicalLadder,
    FolderItemIcon.CircleWithThickSegment,
    FolderItemIcon.ClipboardInBrackets,
    FolderItemIcon.DockBottom,
    FolderItemIcon.FileBarChart,
    FolderItemIcon.FileInBrackets,
    FolderItemIcon.FillGrid,
    FolderItemIcon.FlaskReceipt,
    FolderItemIcon.Flowchart,
    FolderItemIcon.GridDots,
    FolderItemIcon.Instrument,
    FolderItemIcon.LightbulbInBrackets,
    FolderItemIcon.ListAsterisk,
    FolderItemIcon.ListCheckXmark,
    FolderItemIcon.MoleculeToBackbone,
    FolderItemIcon.MoleculeToMolecule,
    FolderItemIcon.NodesConverge,
    FolderItemIcon.OchemMoleculeBeaker,
    FolderItemIcon.OchemMolecule,
    FolderItemIcon.OligoAlt,
    FolderItemIcon.Part,
    FolderItemIcon.Petri,
    FolderItemIcon.Process,
    FolderItemIcon.ProteinAlt,
    FolderItemIcon.ProteinMagnifyingGlass,
    FolderItemIcon.ReceiptFlask,
    FolderItemIcon.RequestTemplate,
    FolderItemIcon.Rna,
    FolderItemIcon.SquareBracketCheckEquals,
    FolderItemIcon.TableListAlt,
    FolderItemIcon.TubeEmpty,
    FolderItemIcon.Vial,
    FolderItemIcon.WellPlate,
    FolderItemIcon.Workflow,
  ],
  [TagSchemaItemType.DNA_SEQUENCE]: [
    FolderItemIcon.Sequence,
    FolderItemIcon.Plasmid,
    FolderItemIcon.OligoV2,
    FolderItemIcon.PrimerPair,
    FolderItemIcon.Linear,
    FolderItemIcon.BasicFolderItem,
    FolderItemIcon.RNASequence,
  ],
  [TagSchemaItemType.ENTRY]: [FolderItemIcon.Entry],
  [TagSchemaItemType.EQUIPMENT]: [FolderItemIcon.Equipment],
  [TagSchemaItemType.MIXED]: [
    FolderItemIcon.Sequence,
    FolderItemIcon.Plasmid,
    FolderItemIcon.OligoV2,
    FolderItemIcon.PrimerPair,
    FolderItemIcon.Linear,
    FolderItemIcon.BasicFolderItem,
    FolderItemIcon.RNASequence,
  ],
  [TagSchemaItemType.MIXTURE]: [
    FolderItemIcon.Mixture,
    FolderItemIcon.MortarPestle,
    FolderItemIcon.Powder,
    FolderItemIcon.PowderBeaker,
    FolderItemIcon.Recipe,
    FolderItemIcon.Solution,
  ],
  [TagSchemaItemType.MOLECULE]: [FolderItemIcon.SmallMolecule, FolderItemIcon.SmallMoleculeBeaker],
  [TagSchemaItemType.DNA_OLIGO]: [FolderItemIcon.OligoV2, FolderItemIcon.Oligo, FolderItemIcon.Linear],
  [TagSchemaItemType.OLIGO_CONJUGATE]: [FolderItemIcon.OligoConjugate],
  [TagSchemaItemType.OLIGO_DUPLEX]: [FolderItemIcon.OligoConjugate],
  [TagSchemaItemType.RNA_OLIGO]: [
    FolderItemIcon.OligoV2,
    FolderItemIcon.Oligo,
    FolderItemIcon.Linear,
    FolderItemIcon.Sequence,
  ],
  [TagSchemaItemType.RNA_SEQUENCE]: [
    FolderItemIcon.RNASequence,
    FolderItemIcon.Sequence,
    FolderItemIcon.OligoV2,
    FolderItemIcon.PrimerPair,
    FolderItemIcon.Linear,
    FolderItemIcon.BasicFolderItem,
  ],
  [TagSchemaItemType.TRIAL_SUBJECT]: [FolderItemIcon.BasicFolderItem],
};
